<template>
<!-- eslint-disable max-len -->
<edit-page
  name="Playthrough"
  model-name="group"
  param="groupId"
  page-prefix="/blade/playthroughs"
  :create="createGroup"
  :fetch="getGroupWrapper"
  :fetch-types="getGroupTypesWrapper"
  :update="updateGroup"
  :remove="deleteGroup"
  :enum-names="['games', 'classes', 'roles', 'users', 'groupStatuses']"
  :stub="group"
  :validator="validator"
  :utils="utils"
  :watchers="watchers">
<template slot-scope="{ model: group, enums: { games, classes, roles, users, groupStatuses }, utils: { unused }, validator, creating, editing }">
  <text-input label v-model.trim="group.name" :validator="validator.name" required autofocus>Name</text-input>

  <text-input label v-model.trim="group.title" :validator="validator.title" required>Title</text-input>

  <select-input label v-model="group.status" :validator="validator.status" :options="groupStatuses" required :disabled="wasComplete">Status {{ wasStarted ? '(previously started)' : '' }}</select-input>

  <select-input label v-model="group.activeClass" :disabled="wasStarted" :options="classes" use="_id" display="courseName" :to="(id) => { return `/blade/classes/${id}/edit`; }">{{classTypeName}}</select-input>

  <b-input-group v-if="canManageAllGroups" style="width: 100%;">
    <b-input-group-prepend>
      <b-input-group-text>Copy Playthrough to Class/Workgroup</b-input-group-text>
    </b-input-group-prepend>
    <b-select :options="classCopyOptions" v-model="classCopyTargetId"/>
    <b-input-group-append>
      <b-btn @click.prevent="copyToClass" :disabled="!classCopyTargetId || isCopyingToClass">Copy</b-btn>
    </b-input-group-append>
  </b-input-group>
  <a :href="`/blade/playthroughs/${playthroughCopyResultId}/edit`" target="_blank" v-if="playthroughCopyResultId">View Copied Class/Workgroup</a>

  <select-input label v-model="group.game" :disabled="wasStarted" :validator="validator.game" :options="games" use="_id" display="name" required :to="(id) => { return `/blade/games/${id}/edit`; }">Gamulation</select-input>

  <array-input :items="group.players" :validator="validator.players" required :max-length="group.game && gamesById[group.game] && gamesById[group.game].roles.length || 0" :disabled="wasStarted || group.players.length > 0 && (validator.players.$invalid || group.players.length >= group.roles.length)">
    <h5 slot="title">Players</h5>

    <select-input slot="item" slot-scope="{ item: player, index: i }" :disabled="wasStarted" v-model="player._id" :validator="validator.players.$each[i]._id" :options="unused(users).by('_id').in(group.players).by('_id').at(i)" use="_id" display="email" required autofocus :remove="wasStarted ? null : () => { group.players.splice(i, 1); }" :to="(id) => { return `/blade/users/${id}/edit`; }">Player</select-input>
    <span slot="button">
      <template v-if="wasStarted || group.players.length >= group.roles.length">Can't add Players</template>
      <template v-else>Add Player</template>
    </span>
  </array-input>

  <array-input :items="group.roles" :validator="validator.roles" disabled :custom-errors="[{ validator: 'allPlayersAssigned', message: 'All players must be assigned a primary role' }, { validator: 'allRolesAssigned', message: 'All roles must be assigned if starting playthrough' }]">
    <h5 slot="title">Roles</h5>

    <template slot="item" slot-scope="{ item: role, index: i }">
      <select-input v-model="role._id" :options="roles" use="_id" display="name" disabled :to="(id) => { return `/blade/roles/${id}/edit`; }">Role</select-input>

      <h6>Player / PlayedBy / AI Player</h6>
      <div class="form-row">
        <select-input class="col-sm-4" v-model="role.player" :disabled="wasStarted || !!role.playedBy || role.ai" :options="unused(users.filter(({ _id: userId }) => group.players.some(({ _id: playerId }) => playerId === userId))).by('_id').in(group.roles).by('player').at(i)" use="_id" display="email" :to="(id) => { return `/blade/users/${id}/edit`; }">Player</select-input>

        <select-input class="col-sm-4" v-model="role.playedBy" :disabled="wasStarted || !!role.player || role.ai" :options="users.filter(({ _id: userId }) => group.players.some(({ _id: playerId }) => playerId === userId))" use="_id" display="email" :to="(id) => { return `/blade/users/${id}/edit`; }">Played By</select-input>

        <checkbox-input class="col-sm-4" v-model="role.ai" :disabled="wasStarted || !!role.player || !!role.playedBy">AI Player</checkbox-input>
      </div>
      <hr v-if="i < group.roles.length - 1">
    </template>
    <span slot="button">Can't add Roles</span>
  </array-input>

  <button v-if="editing && wasStarted" class="btn btn-secondary" @click.prevent="reset">Reset Playthrough</button>
</template>
</edit-page>
</template>

<script>
import { required, requiredIf } from 'vuelidate/lib/validators';
import { mapActions, mapGetters } from 'vuex';

import { unused } from '../../../utils/utils';

import EditPage from '../../ui/pages/EditPage';

import ArrayInput from '../../ui/inputs/ArrayInput';
import CheckboxInput from '../../ui/inputs/CheckboxInput';
import NumberInput from '../../ui/inputs/NumberInput';
import SelectInput from '../../ui/inputs/SelectInput';
import TextInput from '../../ui/inputs/TextInput';

export default {
  components: {
    EditPage,
    ArrayInput,
    CheckboxInput,
    NumberInput,
    SelectInput,
    TextInput
  },
  data() {
    return {
      utils: {
        unused
      },
      isCopyingToClass: false,
      classCopyTargetId: null,
      playthroughCopyResultId: null,
      gamesById: {},
      wasStarted: false,
      wasComplete: false,
      validCopyTargets: [],
      mountedGroup: null,
      group: {
        _id: '',
        name: '',
        title: '',
        status: 'pending',
        activeClass: '',
        game: '',
        players: [],
        roles: []
      },
      validator: {
        name: { required },
        title: { required },
        status: { required },
        game: {
          required: requiredIf(({ status }) => status !== 'pending')
        },
        players: {
          required: requiredIf(({ status }) => status !== 'pending'),
          maxLength: (value, { roles }) => (
            !roles.length || value.length <= roles.length
          ),
          $each: {
            _id: { required }
          }
        },
        // disable roles validation for now
        roles: {
          allPlayersAssigned: (value, { players, status }) => (
            true ||
            status === 'pending' ||
            value.reduce((acc, { player }) => (
              player ? acc + 1 : acc
            ), 0) === players.length
          ),
          allRolesAssigned: (value, { status }) => (
            true ||
            status === 'pending' ||
            value.every(role => role.player || role.playedBy || role.ai)
          )
        }
      },
      watchers: [{
        field: 'model.game',
        watcher: (model, newValue) => {
          if (!newValue) {
            if (!this.wasStarted) this.group.roles = [];
            return;
          }
          const game = this.gamesById[newValue];
          if (!game) {
            if (!this.wasStarted) this.group.roles = [];
            return;
          }
          model.roles = game.roles.map(role => (
            model.roles.find(({ _id }) => _id === role._id) || role
          ));
        }
      }]
    };
  },
  computed: {
    ...mapGetters([
      'classTypeName',
      'canManageAllGroups',
    ]),
    classCopyOptions() {
      if (!this.mountedGroup) return [];

      let foundNonCurrent = false;
      // eslint-disable-next-line no-shadow
      const intermediate = [{
        value: null,
        text: 'Select a Class/Workgroup to copy this Playthrough to',
        disabled: true
      }].concat(this.validCopyTargets.map((course) => {
        if (course._id === this.mountedGroup.activeClass) {
          return {
            value: null,
            text: `${course.className || course.courseName} (current)`,
            disabled: true
          };
        }
        foundNonCurrent = true;
        return ({
          value: course._id,
          text: course.className || course.courseName
        });
      }));

      if (!foundNonCurrent) {
        return [{
          value: null,
          text: 'No valid Classes/Workgroups to copy this Playthrough to.  Make sure that another class/workgroup exists for this game.',
          disabled: true
        }];
      }
      return intermediate;
    }
  },
  methods: {
    ...mapActions([
      'getGroup',
      'getGroupTypes',
      'createGroup',
      'updateGroup',
      'deleteGroup',
      'resetGroup',
      'getClassesByGame',
      'copyGroupToClass',
      'flashSuccess',
      'flashDanger'
    ]),
    hashById(items) {
      return items.reduce((acc, item) => {
        acc[item._id] = item;
        return acc;
      }, {});
    },
    async getGroupWrapper(idParam) {
      const data = await this.getGroup(idParam);
      this.gamesById = this.hashById(data.games);
      this.wasStarted = data.group.teamDecisions.length > 0;
      this.wasComplete = data.group.status === 'complete';
      return data;
    },
    async getGroupTypesWrapper() {
      const data = await this.getGroupTypes();
      this.gamesById = this.hashById(data.games);
      return data;
    },
    async reset() {
      // eslint-disable-next-line no-alert
      if (!window.confirm('Do you really want to reset the Playthrough?')) return;
      const { groupId } = this.$route.params;
      await this.resetGroup({ groupId });
      window.location.href = this.$route.path;
    },
    async copyToClass() {
      try {
        this.isCopyingToClass = true;
        const { groupId } = this.$route.params;
        const { classCopyTargetId } = this;
        if (!classCopyTargetId) {
          this.flashDanger({ message: 'Please select a class/workgroup to copy to' });
          return;
        }

        if (classCopyTargetId === this.mountedGroup.activeClass) {
          this.flashDanger({ message: 'Cannot copy to the current class/workgroup' });
          return;
        }

        try {
          const result = await this.copyGroupToClass({ groupId, classId: classCopyTargetId });
          console.log(result);

          this.flashSuccess({ message: 'Successfully copied group.  Use the link below the copy controls to view the new group.' });
          this.playthroughCopyResultId = result._id;
        } catch (err) {
          console.log(err);
          if (err.response && err.response.data) {
            this.flashDanger({ message: `Failed to copy group: ${err.response.data}` });
          } else {
            this.flashDanger({ message: 'Failed to copy group: Unknown error' });
          }
        }
      } finally {
        this.isCopyingToClass = false;
      }
    }
  },
  async mounted() {
    const { groupId } = this.$route.params;
    if (groupId) {
      this.validCopyTargets.length = 0;
      const { group } = await this.getGroup({ groupId });
      this.mountedGroup = group;
      const gameId = group.game;

      const { classes } = await this.getClassesByGame({ gameId });
      if (classes && Array.isArray(classes) && classes.length) {
        this.validCopyTargets.push(...classes);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
</style>



// WEBPACK FOOTER //
// src/components/blade/pages/GroupEdit.vue